import { NgModule } from "@angular/core";
import { ExtraOptions, RouterModule } from "@angular/router";
import { AccountsComponent } from "./accounts-v2/accounts.component";
import { AppMainComponent } from "./app.main.component";
import { CasesGuardService as CasesGuard } from "./cases/cases-guard.service";
import { ConfigurationsComponent } from "./configurations/configurations.component";
import { ForgotPasswordComponent } from "./forgot-password/forgot-password.component";
import { InventoryComponent } from "./inventory/inventory.component";
import { QuotationComponent } from "./inventory/quotation/quotation.component";
import { AuthGuard } from "./login/auth.guard";
import { LoggedGuard } from './login/logged.guard';
import { LoginComponent } from "./login/login.component";
import { ModuleGuardService } from './service/module.guard.service';
import { MoreComponent } from "./more/more.component";
import { AppAccessdeniedComponent } from "./pages/app.accessdenied.component";
import { AppErrorComponent } from "./pages/app.error.component";
import { AppNotfoundComponent } from "./pages/app.notfound.component";
import { GuideComponent } from './guide/guide.component';
import { MassEditComponent } from './misc/mass-edit/mass-edit.component';
import { LocationguardService as locationGuard } from '../app/inventory/locations/locationguard.service';
import { ProductGuardService as productGuard } from '../app/inventory/products/product-guard.service';
import { PurchaseguardService as purchaseGuard } from '../app/inventory/purchases/purchaseguard.service';
import { OrdersguardService as ordersGuard } from './inventory/sales-order/services/ordersguard.service';
import { ObjectNames } from './modules/shared/constants/object.names';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { SerialDetailsComponent } from "./modules/shared/components/serial-details/serial-details.component";
import { SharedContactsV1Component } from "./modules/shared/components/shared-contacts-v1/shared-contacts-v1.component";
import { DynamicFormExternalComponent } from "./modules/shared/components/dynamic-form-external/dynamic-form-external.component";
import { ForgotOrganizationIDComponent } from "./forgot-organization-id/forgot-organization-id.component";

const routerOptions: ExtraOptions = {
    scrollPositionRestoration: "enabled",
    anchorScrolling: "enabled",
    scrollOffset: [0, 90]
};

@NgModule({
    imports: [
        RouterModule.forRoot(
            [
                {
                    path: "login",
                    component: LoginComponent,
                    canActivate: [LoggedGuard]
                },
                {
                    path: "password",
                    component: ForgotPasswordComponent,
                    canActivate: [LoggedGuard]
                },
                {
                    path: "forgot-organization-id",
                    component: ForgotOrganizationIDComponent,
                    canActivate: [LoggedGuard]
                },
                {
                    path: "reset-password",
                    component: ResetPasswordComponent,
                    canActivate: [LoggedGuard]
                },
                {
                    path: "",
                    component: AppMainComponent,
                    children: [
                        {
                            path: "leads",
                            loadChildren: () =>
                                import(
                                    "./leads-v2/leads.module"
                                ).then((m) => m.LeadsV2Module),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.LEAD, moduleName: 'Lead' }
                        },
                        {
                            path: "accounts",
                            loadChildren: () =>
                                import(
                                    "./accounts-v2/accounts-v2.module"
                                ).then((m) => m.AccountsV2Module),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.ACCOUNT, moduleName: 'Account' }
                        },
                        {
                            path: "account/:id",
                            component: AccountsComponent,
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.ACCOUNT, moduleName: 'Account' }
                        },
                        {
                            path: "opportunities",
                            loadChildren: () =>
                                import(
                                    "./opportunities-v2/opportunities-v2.module"
                                ).then((m) => m.OpportunitiesV2Module),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.OPPORTUNITY, moduleName: 'Opportunity' }
                        },
                        {
                             path: 'contacts', component: SharedContactsV1Component 
                        },
                        {
                            path: "inventory",
                            component: InventoryComponent,
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "quotation",
                            component: QuotationComponent,
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "estimates",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.ESTIMATE, moduleName: 'Estimate' }
                        },
                        {
                            path: "sales-order",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.SALES_ORDER, moduleName: 'SalesOrder' }
                        },
                        {
                            path: "invoicing",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.INVOICE, moduleName: 'Invoice' }
                        },
                        {
                            path: "sales-receipt",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.RECEIVE_PAYMENT, moduleName: 'SalesReceipt' }
                        },
                        {
                            path: "customer-returns",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.Customer_RMA, moduleName: 'Customer RMA' }
                        },
                        {
                            path: "credit-memo",
                            loadChildren: () =>
                                import(
                                    "./inventory/sales-order/sales-order.module"
                                ).then((m) => m.SalesOrderModule),
                            canActivate: [AuthGuard, ordersGuard],
                            data: { moduleObject: ObjectNames.Credit_Memo, moduleName: 'Credit Memo' }
                        },
                        {
                            path: "products",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard]
                        },
                        {
                            path: "inventory-items",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard],
                            data: { moduleObject: ObjectNames.Inventory_Item }
                        },
                        {
                            path: "non-inventory",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard],
                            data: { moduleObject: ObjectNames.Non_Inventory_Item }
                        },
                        {
                            path: "services",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard],
                            data: { moduleObject: ObjectNames.Service_Item }
                        },
                        {
                            path: "bundle-items",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard],
                            data: { moduleObject: ObjectNames.Bundle_Item }
                        },
                        {
                            path: "kit-items",
                            loadChildren: () =>
                                import(
                                    "./inventory/products/products.module"
                                ).then((m) => m.ProductsModule),
                            canActivate: [AuthGuard, productGuard],
                            data: { moduleObject: ObjectNames.Kit_Items }
                        },
                        {
                            path: "serial-details",
                            component: SerialDetailsComponent,
                            canActivate: [AuthGuard],
                            children: [
                                {
                                    path: "activation-detail",
                                    component: SerialDetailsComponent,
                                    canActivate: [AuthGuard],
                                },
                                {
                                    path: "residual-detail",
                                    component: SerialDetailsComponent,
                                    canActivate: [AuthGuard],
                                },
                                {
                                    path: "commission-details",
                                    component: SerialDetailsComponent,
                                    canActivate: [AuthGuard],
                                },
                                {
                                    path: "spiff-details",
                                    component: SerialDetailsComponent,
                                    canActivate: [AuthGuard],
                                },
                            ]
                        },
                        {
                            path: "purchase-order",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.PURCHASE }
                        },
                        {
                            path: "item-receipt",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Item_Receipt }
                        },
                        {
                            path: "bill",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Bill }
                        },
                        {
                            path: "payment-receipt",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Bill_Payment }
                        },
                        {
                            path: "vendors",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Vendor }
                        },
                        {
                            path: "vendor-return",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Vendor_Returns }
                        },
                        {
                            path: "vendor-credit",
                            loadChildren: () =>
                                import(
                                    "./inventory/purchases/purchases.module"
                                ).then((m) => m.PurchasesModule),
                            canActivate: [AuthGuard, purchaseGuard],
                            data: { moduleObject: ObjectNames.Vendor_Credit }
                        },
                        {
                            path: "locations",
                            loadChildren: () =>
                                import(
                                    "./inventory/locations/locations.module"
                                ).then((m) => m.LocationsModule),
                            canActivate: [AuthGuard, locationGuard],
                        },
                        {
                            path: "Finance",
                            loadChildren: () =>
                                import("./finance/finance.module").then(
                                    (m) => m.FinanceModule
                                ),
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "more",
                            component: MoreComponent,
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "territory",
                            loadChildren: () =>
                                import(
                                    "./more/territory/territory.module"
                                ).then((m) => m.TerritoryModule),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.TERRITORY, moduleName: 'Territory' }
                        },
                        {
                            path: "workflow",
                            loadChildren: () =>
                                import("./misc/workflow/workflow.module").then(
                                    (m) => m.WorkflowModule
                                ),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.WORKFLOW, moduleName: 'Workflow' }
                        },
                        {
                            path: "messaging",
                            loadChildren: () => import("./misc/campaign/campaign.module").then((m) => m.CampaignModule),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.CAMPAIGN, moduleName: 'Messaging' }
                        },
                        {
                            path: "workspace",
                            loadChildren: () =>
                                import("./misc/workspace/workspace.module").then(
                                    (m) => m.WorkspaceModule
                                ),
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "saved-searches",
                            loadChildren: () =>
                                import("./misc/saved-searches/saved-searches.module").then(
                                    (m) => m.SavedSearchesModule
                                ),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.SavedSearch, moduleName: 'Saved Search' }
                        },
                        {
                            path: "mass-edit",
                            component: MassEditComponent,
                            canActivate: [AuthGuard],
                        },
                        // {
                        //     path: "goals",
                        //     component: GoalsComponent,
                        //     canActivate: [AuthGuard, GoalGuardService],
                        // },
                        {
                            path: "sales-quota",
                            loadChildren: () =>
                                import("./more/sales-quota/sales-quota.module").then(
                                    (m) => m.SalesQuotaModule
                                ),
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "questionnaire",
                            loadChildren: () =>
                                import("./more/questionnaire/questionnaire.module").then(
                                    (m) => m.QuestionnaireModule
                                ),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.QUESTIONNAIRE, moduleName: 'Questionnaire' }
                        },
                        {
                            path: "cases",
                            loadChildren: () =>
                                import(
                                    "./cases-v2/cases-v2.module"
                                ).then((m) => m.CasesV2Module),
                            canActivate: [AuthGuard, CasesGuard],
                            pathMatch: 'full'
                        },
                        {
                            path: "reports",
                            //component: ReportsComponent,
                            loadChildren: () =>
                                import("./reports/reports.module").then(
                                    (m) => m.ReportsModule
                                ),
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "configurations",
                            component: ConfigurationsComponent,
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "",
                            loadChildren: () =>
                                import("./profile/profile.module").then(
                                    (m) => m.ProfileModule
                                ),
                            canActivate: [AuthGuard],
                        },
                        {
                            path: "user-guide",
                            component: GuideComponent,
                        },
                        {
                            path: "routes",
                            loadChildren: () =>
                                import("./more/route-management/route-management.module").then(
                                    (m) => m.RouteManagementModule
                                ),
                            canActivate: [AuthGuard, ModuleGuardService],
                            data: { moduleObject: ObjectNames.ROUTE, moduleName: 'Route' }
                        },
                        {
                            path: "experiments",
                            loadChildren: () =>
                                import("./experiments/experiments.module").then(
                                    (m) => m.ExperimentsModule
                                ),
                        }
                    ],
                },
                {
                    path: "form/:workbookid/:formid",
                    component: DynamicFormExternalComponent,
                },
                { path: "error", component: AppErrorComponent },
                { path: "access", component: AppAccessdeniedComponent },
                { path: "notfound", component: AppNotfoundComponent },
                { path: "**", redirectTo: "/notfound" },
            ],
            routerOptions,
        ),
    ],
    exports: [RouterModule]
})
export class AppRoutingModule { }
